import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, KeyRound } from "lucide-react";
import { useDeploymentContent } from "@/companion/DeploymentContentContext";
import { groupBoutiquesByMarket } from "@/lib/api";
import { api } from "@/lib/api";
import {
  authenticationOptionsFromJSON,
  credentialToJSON,
  registrationOptionsFromJSON,
} from "@/lib/passkeys";
import "./Login.css";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function Login() {
  const deployment = useDeploymentContent();
  const locked = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  // Capture the one-time invitation exactly once. We remove it from the visible
  // URL below so it is not left in browser history, but it must remain available
  // in memory for the registration ceremony across subsequent renders.
  const [invitation] = useState(() => new URLSearchParams(window.location.search).get("invite") || "");
  const [email, setEmail] = useState("");
  const [useEmail, setUseEmail] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (invitation) window.history.replaceState({}, document.title, "/login");
  }, [invitation]);

  const completePasskeySession = async () => {
    if (mounted.current) window.location.href = "/home";
  };

  const handlePasskey = async () => {
    if (locked.current) return;
    if (!window.PublicKeyCredential || !navigator.credentials) {
      setError("This browser does not support passkeys.");
      return;
    }
    locked.current = true;
    setBusy(true);
    setError("");
    try {
      if (invitation) {
        const options = await api.post("/auth/passkey/register/options", { invitation });
        const credential = await navigator.credentials.create({
          publicKey: registrationOptionsFromJSON(options.data.publicKey),
        });
        await api.post("/auth/passkey/register/verify", {
          invitation,
          ceremony_id: options.data.ceremony_id,
          credential: credentialToJSON(credential),
          label: "Primary passkey",
        });
        await completePasskeySession();
      } else {
        if (useEmail && !email.trim()) throw new Error("Enter your company email address.");
        const identity = useEmail ? { email: email.trim() } : {};
        const options = await api.post("/auth/passkey/login/options", identity);
        const credential = await navigator.credentials.get({
          publicKey: authenticationOptionsFromJSON(options.data.publicKey),
        });
        await api.post("/auth/passkey/login/verify", {
          ...identity,
          ceremony_id: options.data.ceremony_id,
          credential: credentialToJSON(credential),
        });
        await completePasskeySession();
      }
    } catch (err) {
      if (err?.name === "NotAllowedError") setError("Passkey sign-in was cancelled or timed out.");
      else setError(err?.response?.data?.detail || err?.message || "Passkey sign-in could not be completed.");
    } finally {
      locked.current = false;
      setBusy(false);
    }
  };

  const boutiques = groupBoutiquesByMarket().flatMap((group) => group.boutiques);

  return (
    <main className="login-page">
      <div className="login-vessel">
      <section className="login-introduction" aria-labelledby="login-story-title">
        <div className="login-identity" role="img" aria-label="TOTO, Rainbow Investments, LLC" data-testid="login-wordmark">
          <img className="login-mascot" src="/brand/toto-naturalistic-v4/standing-720.webp" srcSet="/brand/toto-naturalistic-v4/standing-360.webp 360w, /brand/toto-naturalistic-v4/standing-720.webp 720w, /brand/toto-naturalistic-v4/standing-1024.webp 1024w" sizes="(max-width:900px) 127px, 242px" width="1024" height="1024" decoding="async" alt="" />
          <div className="login-wordmarks">
            <img className="login-toto-wordmark" src="/brand/login/toto-wordmark-embossed.svg" alt="" />
            <img className="login-organization-wordmark" src="/brand/login/rainbow-wordmark.svg" alt="" />
          </div>
        </div>
        <div className="login-introduction__statement">
          <h1 id="login-story-title">A quiet companion for the people who look after everyone else.</h1>
          <p className="login-copy">TOTO creates a shared space for owners, leadership, management, and their teams to understand and move within the daily operating rhythm of their organization.</p>
        </div>
      </section>

      <section className="sign-in-vessel" aria-labelledby="sign-in-title">
        <div className="sign-in-content">
          <div className="sign-in-details">
          <div className="sign-in-heading"><h2 id="sign-in-title">{invitation ? "Create your passkey." : "Welcome back."}</h2></div>
          {!invitation && <p className="sign-in-intro">Everything is here when you need it.</p>}
          {invitation && <p className="sign-in-intro">Create the passkey that will protect your invited TOTO account.</p>}
          <div className="sign-in-form">
            {!invitation && useEmail && <label>
              <span>Enter the email address linked to your account.</span>
<div className="login-email-rim"><input type="email" aria-describedby="login-email-help" aria-invalid={!!error} disabled={busy} autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") handlePasskey(); }} placeholder="name@example.com" /></div><small id="login-email-help">Use your company email. Your passkey verifies your account.</small>
            </label>}

            <button aria-label={invitation ? "Create passkey" : "Sign in with passkey"} aria-busy={busy || undefined} onClick={handlePasskey} disabled={busy} data-testid={invitation ? "btn-create-passkey" : "btn-passkey-login"} className="sign-in-button">
              <span className="login-action-label"><KeyRound size={27} aria-hidden="true" />{busy ? "Verifying…" : invitation ? "Create passkey" : "Sign in with passkey"}
              {!busy && <ArrowRight size={27} strokeWidth={1.5} aria-hidden="true" />}</span>
            </button>
            {!invitation && <p className="login-passkey-support">Choose your saved passkey to continue.</p>}
            {!invitation && <button type="button" className="login-email-toggle" disabled={busy} onClick={() => { setUseEmail(value => !value); setError(""); }}>
              {useEmail ? "Use passkey without email" : "Use company email instead"}
            </button>}
            <span className="login-auth-status" role="status">{busy ? "Verifying your passkey…" : ""}</span>
            {error && <p role="alert" className="sign-in-error">{error}</p>}
          </div>
          </div>

        </div>
      </section>
      <footer className="login-footer">
        <section className="login-mission" data-testid="login-mission"><span>Our why</span><p>Giving guests memorable experiences while at the same time delivering joy to our team members, vendors, and the community.</p></section>
        <section className="login-organization" aria-label="Our boutiques">
          <p className="login-legal">{deployment.organizationLegalName}</p>
        <ul className="login-boutiques" aria-label="Rainbow boutiques">
          {boutiques.map((boutique) => (
            <li key={boutique.code} className={`boutique-identifier boutique-identifier--${boutique.code.toLowerCase()}`}>
              <span className={`login-boutique-dot ${boutique.dot}`} aria-hidden="true" />
              <span>{boutique.shortName}</span>
            </li>
          ))}
        </ul>

        </section>
      </footer>
      </div>
    </main>
  );
}
